import type { Table } from "@tanstack/react-table";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn, generatePaginationRange } from "../../lib/utils";
import {
  DEFAULT_PAGE_SIZES,
  PageSelector,
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "../pagination";

type DataTablePaginationProps<T extends { id: unknown }> = {
  table: Table<T>;
  entityLabel: string;
  isLoading?: boolean;
};

export const DataTablePagination = <T extends { id: unknown }>({
  table,
  entityLabel,
  isLoading = false,
}: DataTablePaginationProps<T>) => {
  const { t } = useTranslation();
  const currentPageIndex = table.getState().pagination.pageIndex;
  const previousPageIndexRef = useRef(currentPageIndex);
  const shouldScrollRef = useRef(false);
  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Mark that we should scroll when page changes
    if (currentPageIndex !== previousPageIndexRef.current) {
      previousPageIndexRef.current = currentPageIndex;
      shouldScrollRef.current = true;
    }
    // Wait for DOM to update before scrolling
    if (!isLoading && shouldScrollRef.current) {
      requestAnimationFrame(() => {
        // Find the table header automatically
        const tableHeader =
          paginationRef.current?.previousElementSibling?.querySelector("th");
        if (tableHeader) {
          tableHeader.scrollIntoView({
            block: "start",
          });
        }
        shouldScrollRef.current = false;
      });
    }
  }, [currentPageIndex, isLoading]);

  const displayPagination = table.getRowCount() > DEFAULT_PAGE_SIZES[0];

  return (
    <div
      ref={paginationRef}
      className={cn(
        "h-8 flex border-t border-gray-200 bg-white rounded-b px-4",
        "items-center justify-between w-full",
        isLoading && "pointer-events-none",
      )}
    >
      <Pagination
        className={cn(
          "relative flex items-center justify-center",
          isLoading && "opacity-50",
        )}
      >
        {displayPagination && (
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => {
                  table.previousPage();
                }}
                disabled={!table.getCanPreviousPage()}
                label={t("previous_page")}
              />
            </PaginationItem>
            {generatePaginationRange(
              table.getState().pagination.pageIndex,
              table.getPageCount(),
            ).map((item, idx, array) =>
              item === "ellipsis" ? (
                <PaginationItem
                  key={
                    idx === 1
                      ? "ellipsis-start"
                      : `ellipsis-end-${array[array.length - 1]}`
                  }
                >
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationButton
                    onClick={() => {
                      table.setPageIndex(item);
                    }}
                    isActive={item === table.getState().pagination.pageIndex}
                  >
                    {item + 1}
                  </PaginationButton>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => {
                  table.nextPage();
                }}
                disabled={!table.getCanNextPage()}
                label={t("next_page")}
              />
            </PaginationItem>
          </PaginationContent>
        )}
      </Pagination>

      <PageSelector
        pageSize={table.getState().pagination.pageSize}
        currentPage={table.getState().pagination.pageIndex + 1}
        totalCount={table.getRowCount()}
        pageSizeOptions={DEFAULT_PAGE_SIZES}
        onPageSizeChange={table.setPageSize}
        className={cn(isLoading && "opacity-50")}
        entityLabel={entityLabel}
      />
    </div>
  );
};
